import {openNewDownloadModal} from '@Modals/download-modal/download-modal.library';
import {getIsAiChatPremiumOnly, showAiChatPremiumOnlyFeatureDialog, showPurchaseCreditsDialog} from '@Libraries/ai-chat.library';
import {openCreditPackCheckout} from '@Components/ai-chat-window/lib/ai-chat-credit-checkout.library';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import type {AiChatSizedDownloadSource} from '@Libraries/ga-events';
import {AiChatSizedDownloadRender, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {ImageQualityOption} from '@Components/ai-chat-window/lib/chat-button-actions.types';
import {getTeamCredits, refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import {isUserPremium} from '@Libraries/user.library';
import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {AiChatCostType} from '@Libraries/ai-chat.library';
import {dispatchAiChatAssetDownloaded} from '@Components/ai-chat-window/lib/ai-chat-asset-downloaded.library';
import type {AiChatTargetSize} from '@Components/ai-chat-window/ai-chat-window.types';
import {ExactSizeDownloadFailureCode, FailedMessageType} from '@Components/ai-chat-window/ai-chat-window.types';
import {formatTargetSizeLabel} from '@Libraries/ai-chat-target-size.library';
import {pollableJob} from '@Libraries/pollable-job.library';
import type {PollEnvelope, PollKickoffAck, PollTransportParams} from '@Libraries/pollable-job.library.types';
import {PollFailError, PollState} from '@Libraries/pollable-job.library.types';
import {isPlainObject} from '@Utils/object.util';
import {assertNever} from '@Utils/assert-never.util';
import type {AiChatImageDownloadRequest} from './ai-chat-downloads.library.types';

export enum AiChatImageDownloadEvent {
  STARTED = 'ai-chat-image-download-started',
  COMPLETED = 'ai-chat-image-download-completed',
}

/** PollJobType::AI_CHAT_EXACT_SIZE_DOWNLOAD's fallback deadline, which the server reports as expired only to a poll after it. */
const SIZED_DOWNLOAD_SERVER_DEADLINE_MS = 180_000;
const POLL_PAST_SERVER_DEADLINE_MS = 30_000;
const SIZED_DOWNLOAD_POLL_TIMEOUT_MS = SIZED_DOWNLOAD_SERVER_DEADLINE_MS + POLL_PAST_SERVER_DEADLINE_MS;

interface ExactSizeDownloadResult {
  url: string;
}

interface SizedDownloadRequest {
  chatId: string;
  hashedId: string;
  size: AiChatTargetSize;
  fileType: FileTypeOptions;
  source: AiChatSizedDownloadSource;
}

export interface AiChatImageDownloadContext {
  aiChatImageId: string;
  chatId: string;
}

export interface AiChatImageDownloadStartedEventDetail {
  chatId: string;
  hashedId: string;
}

export interface AiChatImageDownloadCompletedEventDetail {
  chatId: string;
  hashedId: string;
  isEnhanced: boolean;
  success: boolean;
}

export const openAiChatImageDownloadModal = (aiChatImageId: string, chatId: string): void => {
  openNewDownloadModal({
    id: 'ai-chat-image-download',
    aiChatImageDownloadContext: {
      aiChatImageId,
      chatId,
    },
    modalClassName: 'radius-12',
  });
};

/** A design the user already owns goes through the same paid download as My Designs, never the AI-image path. */
export const openUserDesignDownloadModal = (posterHashedId: string, pageHashedId: string | null): void => {
  openNewDownloadModal({
    id: 'download-dialog',
    posterHashedId,
    isOpenedInEditor: false,
    isOpenedInModal: true,
    isOpenedInPrintShop: false,
    defaultOptions: pageHashedId ? {pagesHashedIds: [pageHashedId]} : undefined,
  });
};

export const checkIfAiChatImageHasUpscaledVersion = async (imageHashedId: string): Promise<boolean> => {
  const result = (await window.PMW.readLocal('ai/checkIfAiChatImageHasUpscaledVersion', {imageHashedId})) as {hasUpscaledVersion: boolean};
  return result.hasUpscaledVersion;
};

const trackDownloadAnalytics = (fileType: FileTypeOptions, isEnhanced: boolean): void => {
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_DOWNLOAD, {
    [GA4EventParamName.TYPE]: fileType === FileTypeOptions.PNG ? 'PNG' : 'PDF',
    [GA4EventParamName.VALUE]: isEnhanced ? 'enhanced' : 'original',
  });
};

const showDownloadPremiumOnlyDialog = (): void => {
  showAiChatPremiumOnlyFeatureDialog(true);
};

const ensureAiChatPremiumAccess = async (): Promise<boolean> => {
  if ((await getIsAiChatPremiumOnly()) && !isUserPremium()) {
    showDownloadPremiumOnlyDialog();
    return false;
  }

  return true;
};

const ensureUpscaleCredits = async (): Promise<boolean> => {
  if (!(await ensureAiChatPremiumAccess())) {
    return false;
  }

  await refreshTeamCreditsInStore();
  const {credits} = await getTeamCredits();
  if (credits < (AiChatCostType.UPSCALE_IMAGE_COST as number)) {
    const purchaseData = await showPurchaseCreditsDialog();

    if (!purchaseData?.creditsV2) {
      return false;
    }

    if (purchaseData.creditsV2 < (AiChatCostType.UPSCALE_IMAGE_COST as number)) {
      return false;
    }
  }

  return true;
};

const triggerBrowserDownloadFromUrl = async (url: string, filename: string): Promise<void> => {
  const response = await fetch(url, {mode: 'cors'});
  if (!response.ok) {
    throw new Error(`Failed to download AI chat image: HTTP ${response.status}`);
  }

  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(blobUrl);
};

const notifyServerOfDownload = (hashedId: string): void => {
  void window.PMW.writeLocal('ai/handleAiChatImageAssetDownload', {
    imageHashedId: hashedId,
  }).catch((error: unknown) => {
    console.error('Failed to create poster for downloaded image:', error);
  });
};

const fetchAndSaveAsset = async (hashedId: string, quality: ImageQualityOption, fileType: FileTypeOptions): Promise<void> => {
  const result = (await window.PMW.writeLocal('ai/getAiChatAssetDownloadUrl', {
    imageHashedId: hashedId,
    imageQuality: quality,
    fileType,
  })) as {url: string};

  await saveDownloadedAsset(result.url, hashedId, fileType);
};

const saveDownloadedAsset = async (url: string, hashedId: string, fileType: FileTypeOptions): Promise<void> => {
  await triggerBrowserDownloadFromUrl(url, getDownloadFileNameForGeneratedImageAsset(hashedId, fileType));
  notifyServerOfDownload(hashedId);
};

const dispatchDownloadStartedEvent = (chatId: string, hashedId: string): void => {
  window.dispatchEvent(
    new CustomEvent<AiChatImageDownloadStartedEventDetail>(AiChatImageDownloadEvent.STARTED, {
      detail: {chatId, hashedId},
    })
  );
};

const dispatchDownloadCompletedEvent = (chatId: string, hashedId: string, isEnhanced: boolean, success: boolean): void => {
  window.dispatchEvent(
    new CustomEvent<AiChatImageDownloadCompletedEventDetail>(AiChatImageDownloadEvent.COMPLETED, {
      detail: {chatId, hashedId, isEnhanced, success},
    })
  );
};

/** Tells the user an AI chat download failed. */
export const showAiChatDownloadFailedGrowl = (): void => {
  showMessageGrowl({
    text: window.i18next.t('pmwjs_something_went_wrong'),
    key: 'downloadedAiChatModelAssetError',
    interval: 3000,
    showCloseIcon: true,
    type: GROWL_TYPE.DANGER,
  });
};

const showSizeNeedsRelayoutGrowl = (): void => {
  showMessageGrowl({
    text: window.i18next.t('pmwjs_ai_chat_size_needs_relayout'),
    key: 'aiChatSizedDownloadNeedsRelayout',
    interval: 5000,
    showCloseIcon: true,
    type: GROWL_TYPE.DANGER,
  });
};

const isPollFailure = (error: unknown, code: string): boolean => error instanceof PollFailError && error.code === code;

/** Running out of credits or premium access is not a failed download: the checkout or upgrade is the way forward. */
const showSizedDownloadFailure = (error: unknown): void => {
  if (isPollFailure(error, FailedMessageType.CREDITS_EXHAUSTED)) {
    openCreditPackCheckout();
    return;
  }

  if (error instanceof PollFailError && isExactSizeDownloadFailureCode(error.code)) {
    showExactSizeDownloadFailure(error.code);
    return;
  }

  showAiChatDownloadFailedGrowl();
};

const isExactSizeDownloadFailureCode = (code: string): code is ExactSizeDownloadFailureCode =>
  Object.values(ExactSizeDownloadFailureCode).some((failureCode: string): boolean => failureCode === code);

const showExactSizeDownloadFailure = (code: ExactSizeDownloadFailureCode): void => {
  switch (code) {
    case ExactSizeDownloadFailureCode.PREMIUM_ONLY:
      showDownloadPremiumOnlyDialog();
      return;
    case ExactSizeDownloadFailureCode.SIZE_NOT_IMAGE_SHAPE:
      showSizeNeedsRelayoutGrowl();
      return;
    case ExactSizeDownloadFailureCode.IMAGE_NOT_AVAILABLE:
      showAiChatDownloadFailedGrowl();
      return;
    default:
      assertNever(code);
  }
};

const reportDownloadFailure = (error: unknown, showFailure: (error: unknown) => void): void => {
  console.error('Failed to download AI chat image:', error);
  showFailure(error);
};

interface RunDownloadWithNotificationsParams {
  chatId: string;
  hashedId: string;
  isEnhanced: boolean;
  loadingText: string;
  performDownload: () => Promise<void>;
  showFailure: (error: unknown) => void;
}

const runDownloadWithNotifications = async ({chatId, hashedId, isEnhanced, loadingText, performDownload, showFailure}: RunDownloadWithNotificationsParams): Promise<void> => {
  let isStartEventDispatched = false;
  let isSuccess = false;

  try {
    dispatchDownloadStartedEvent(chatId, hashedId);
    isStartEventDispatched = true;

    showLoading('downloadingAiChatModelAsset', {
      text: loadingText,
    });

    await performDownload();

    showMessageGrowl({
      text: window.i18next.t('pmwjs_file_downloaded'),
      key: 'downloadedAiChatModelAsset',
      interval: 2000,
      showCloseIcon: true,
      type: GROWL_TYPE.SUCCESS,
    });

    isSuccess = true;
    dispatchAiChatAssetDownloaded(chatId);
  } catch (error: unknown) {
    reportDownloadFailure(error, showFailure);
  } finally {
    hideLoading('downloadingAiChatModelAsset');
    if (isStartEventDispatched) {
      dispatchDownloadCompletedEvent(chatId, hashedId, isEnhanced, isSuccess);
    }
  }
};

export const downloadAiChatImage = async (request: AiChatImageDownloadRequest): Promise<void> => {
  if (request.targetSize === null) {
    await downloadGeneratedImageAsset(request.chatId, request.hashedId, request.quality, request.fileType);
    return;
  }

  await downloadExactSizeImageAsset(request.chatId, request.hashedId, request.targetSize, request.fileType, request.source);
};

export const downloadGeneratedImageAsset = async (chatId: string, hashedId: string, quality: ImageQualityOption, fileType: FileTypeOptions): Promise<void> => {
  const isEnhanced = quality === ImageQualityOption.ENHANCED_4K;
  const isAlreadyUpscaled = isEnhanced ? await checkIfAiChatImageHasUpscaledVersion(hashedId) : false;

  trackDownloadAnalytics(fileType, isEnhanced);

  if (isEnhanced && !isAlreadyUpscaled) {
    const canProceed = await ensureUpscaleCredits();
    if (!canProceed) {
      return;
    }
  }

  await runDownloadWithNotifications({
    chatId,
    hashedId,
    isEnhanced,
    loadingText: window.i18next.t('pmwjs_downloading'),
    performDownload: () => fetchAndSaveAsset(hashedId, quality, fileType),
    showFailure: showAiChatDownloadFailedGrowl,
  });
};

/** The server renders the size and charges any upscale; isEnhanced on the completed event says the upscaled master now exists. */
export const downloadExactSizeImageAsset = async (
  chatId: string,
  hashedId: string,
  size: AiChatTargetSize,
  fileType: FileTypeOptions,
  source: AiChatSizedDownloadSource
): Promise<void> => {
  if (!(await mayStartSizedDownloadOrReportFailure(hashedId, size))) {
    return;
  }

  const request: SizedDownloadRequest = {chatId, hashedId, size, fileType, source};
  await runDownloadWithNotifications({
    chatId,
    hashedId,
    isEnhanced: size.requiresUpscale,
    loadingText: window.i18next.t('pmwjs_ai_chat_preparing_sized_download', {size: formatTargetSizeLabel(size)}),
    performDownload: () => prepareAndSaveSizedFile(request),
    showFailure: showSizedDownloadFailure,
  });
};

const mayStartSizedDownloadOrReportFailure = async (hashedId: string, size: AiChatTargetSize): Promise<boolean> => {
  try {
    return await mayStartSizedDownload(hashedId, size);
  } catch (error: unknown) {
    reportDownloadFailure(error, showAiChatDownloadFailedGrowl);
    return false;
  }
};

/** Only a new upscale is premium-only; credits are the server's call, through CREDITS_EXHAUSTED. */
const mayStartSizedDownload = async (hashedId: string, size: AiChatTargetSize): Promise<boolean> => {
  if (!size.requiresUpscale || (await checkIfAiChatImageHasUpscaledVersion(hashedId))) {
    return true;
  }

  return ensureAiChatPremiumAccess();
};

const prepareAndSaveSizedFile = async (request: SizedDownloadRequest): Promise<void> => {
  const {url} = await renderExactSizeFile(request);
  await saveDownloadedAsset(url, request.hashedId, request.fileType);
  trackSizedDownload(GA4EventName.AICHAT_SIZED_DOWNLOAD_COMPLETED, request);
};

const renderExactSizeFile = async (request: SizedDownloadRequest): Promise<ExactSizeDownloadResult> =>
  pollableJob<ExactSizeDownloadResult>({
    kickoff: async (): Promise<PollKickoffAck> => kickoffExactSizeDownload(request),
    status: checkExactSizeDownloadStatus,
    timeoutMs: SIZED_DOWNLOAD_POLL_TIMEOUT_MS,
    onAck: (): void => {
      trackSizedDownload(GA4EventName.AICHAT_SIZED_DOWNLOAD_STARTED, request);
    },
  });

const kickoffExactSizeDownload = async (request: SizedDownloadRequest): Promise<PollKickoffAck> => {
  const ack: unknown = await window.PMW.writeLocal('ai/kickoffExactSizeDownload', {
    imageHashedId: request.hashedId,
    targetSize: request.size.compact,
    fileType: request.fileType,
  });
  if (!isPollKickoffAck(ack)) {
    throw new Error('The exact-size download kickoff did not return a polling key');
  }

  return ack;
};

const checkExactSizeDownloadStatus = async (ack: PollKickoffAck, transportParams: PollTransportParams): Promise<PollEnvelope<ExactSizeDownloadResult>> => {
  const envelope: unknown = await window.PMW.readLocal('ai/exactSizeDownloadStatus', {pollingKey: ack.pollingKey, ...transportParams});
  if (!isExactSizeDownloadEnvelope(envelope)) {
    throw new Error('The exact-size download status is not a poll envelope');
  }

  return envelope;
};

const isPollKickoffAck = (value: unknown): value is PollKickoffAck => isPlainObject(value) && typeof value.pollingKey === 'string';

const isExactSizeDownloadEnvelope = (value: unknown): value is PollEnvelope<ExactSizeDownloadResult> =>
  isPlainObject(value) && Object.values(PollState).some((state): boolean => state === value.state) && (value.data === undefined || isExactSizeDownloadResult(value.data));

const isExactSizeDownloadResult = (value: unknown): value is ExactSizeDownloadResult => isPlainObject(value) && typeof value.url === 'string';

const trackSizedDownload = (eventName: GA4EventName.AICHAT_SIZED_DOWNLOAD_STARTED | GA4EventName.AICHAT_SIZED_DOWNLOAD_COMPLETED, request: SizedDownloadRequest): void => {
  window.PMW.gtm.trackGA4CustomEvent(eventName, {
    [GA4EventParamName.SOURCE]: request.source,
    [GA4EventParamName.TYPE]: request.fileType.toUpperCase(),
    [GA4EventParamName.VALUE]: request.size.requiresUpscale ? AiChatSizedDownloadRender.UPSCALED : AiChatSizedDownloadRender.DIRECT,
  });
};

const getDownloadFileNameForGeneratedImageAsset = (hashedId: string, fileType: FileTypeOptions = FileTypeOptions.PNG): string => {
  const prefix = fileType === FileTypeOptions.PDF ? 'PosterMyWall_GeneratedDocument_' : 'PosterMyWall_GeneratedImage_';
  const trimmedHashedId = hashedId.length > 8 ? hashedId.substring(0, 8) : hashedId;

  return `${prefix}${trimmedHashedId}.${fileType}`;
};
